// src/pages/RebalanceSettingsPage.js
import React, { useState, useEffect } from 'react';
import { usePortfolio } from '../contexts/PortfolioContext';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db, auth } from '../services/firebase/config';
import { defaultRebalanceSettings } from '../services/firebase/settingsService';
import { toast } from 'react-hot-toast';

const RebalanceSettingsPage = () => {
  const { selectedPortfolio } = usePortfolio();
  const [settings, setSettings] = useState(defaultRebalanceSettings);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadSettings = async () => {
      if (!selectedPortfolio?.id || !auth.currentUser?.email) return;

      try {
        // Önce genel ayarları yükle
        const generalSettingsRef = doc(db, 'users', auth.currentUser.email, 'settings', 'rebalance');
        const generalSettingsDoc = await getDoc(generalSettingsRef);
        
        // Sonra portföye özel ayarları yükle
        const portfolioSettingsRef = doc(db, 'users', auth.currentUser.email, 'portfolios', selectedPortfolio.id, 'settings', 'rebalance');
        const portfolioSettingsDoc = await getDoc(portfolioSettingsRef);
        
        // Genel ayarları varsayılan olarak al, varsa portföye özel ayarlarla üzerine yaz
        const generalSettings = generalSettingsDoc.exists() ? generalSettingsDoc.data() : defaultRebalanceSettings;
        const portfolioSettings = portfolioSettingsDoc.exists() ? portfolioSettingsDoc.data() : {};
        
        setSettings({
          ...generalSettings,
          ...portfolioSettings
        });
      } catch (error) {
        console.error('Error loading rebalance settings:', error);
        toast.error('Ayarlar yüklenirken bir hata oluştu');
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [selectedPortfolio?.id]);

  const handleSettingsChange = async (changes) => {
    if (!selectedPortfolio?.id || !auth.currentUser?.email) return;

    try {
      const updatedSettings = { ...settings, ...changes };
      setSettings(updatedSettings);

      // Portföye özel ayarları kaydet
      const portfolioSettingsRef = doc(db, 'users', auth.currentUser.email, 'portfolios', selectedPortfolio.id, 'settings', 'rebalance');
      await setDoc(portfolioSettingsRef, {
        ...updatedSettings,
        updatedAt: new Date(),
        lastModifiedBy: auth.currentUser.email
      });

      toast.success('Ayarlar başarıyla güncellendi');
    } catch (error) {
      console.error('Error updating rebalance settings:', error);
      toast.error('Ayarlar güncellenirken bir hata oluştu');
    }
  };

  const handleInputChange = (field) => (event) => {
    const value = parseFloat(event.target.value);
    if (!isNaN(value)) {
      handleSettingsChange({ [field]: value });
    }
  };

  if (!auth.currentUser?.email) {
    return (
      <div className="max-w-4xl mx-auto py-8 px-4">
        <div className="text-center text-gray-600 dark:text-gray-400">
          Lütfen önce giriş yapın
        </div>
      </div>
    );
  }

  if (!selectedPortfolio) {
    return (
      <div className="max-w-4xl mx-auto py-8 px-4">
        <div className="text-center text-gray-600 dark:text-gray-400">
          Lütfen bir portföy seçin
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto py-8 px-4">
        <div className="text-center text-gray-600 dark:text-gray-400">
          Yükleniyor...
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <div className="bg-white dark:bg-[#1f2544] rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Rebalance Ayarları
          </h2>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Portföy: {selectedPortfolio.name}
          </div>
        </div>

        <div className="space-y-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Sapma Eşiği (%)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={settings.threshold}
              onChange={handleInputChange('threshold')}
              className="w-full p-3 bg-gray-50 dark:bg-[#151934] rounded-md border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
            />
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Portföy dengesi için izin verilen maksimum sapma yüzdesi
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Hedef Bütçe
            </label>
            <input
              type="number"
              min="0"
              value={settings.targetBudget}
              onChange={handleInputChange('targetBudget')}
              className="w-full p-3 bg-gray-50 dark:bg-[#151934] rounded-md border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
            />
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Portföy için belirlenen hedef bütçe miktarı
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Bütçe Uyarı Eşiği (%)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={settings.budgetWarningThreshold}
              onChange={handleInputChange('budgetWarningThreshold')}
              className="w-full p-3 bg-gray-50 dark:bg-[#151934] rounded-md border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
            />
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Hedef bütçeye yaklaşma uyarısı için yüzde değeri
            </p>
          </div>

          {/* Smart Rules */}
          <div className="space-y-4 pt-6 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Akıllı Kurallar</h3>
            
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.smartRules?.preventConflictingActions}
                  onChange={(e) => handleSettingsChange({
                    smartRules: {
                      ...settings.smartRules,
                      preventConflictingActions: e.target.checked
                    }
                  })}
                  className="rounded text-blue-600 focus:ring-blue-500 h-4 w-4"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Çakışan İşlemleri Engelle
                </span>
              </label>
            </div>

            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.smartRules?.prioritizeParentActions}
                  onChange={(e) => handleSettingsChange({
                    smartRules: {
                      ...settings.smartRules,
                      prioritizeParentActions: e.target.checked
                    }
                  })}
                  className="rounded text-blue-600 focus:ring-blue-500 h-4 w-4"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Ana Kategori İşlemlerine Öncelik Ver
                </span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Minimum İşlem Tutarı
              </label>
              <input
                type="number"
                value={settings.smartRules?.minTransactionAmount}
                onChange={(e) => handleSettingsChange({
                  smartRules: {
                    ...settings.smartRules,
                    minTransactionAmount: Number(e.target.value)
                  }
                })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-[#1f2544] dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Maksimum İşlem Yüzdesi (%)
              </label>
              <input
                type="number"
                value={settings.smartRules?.maxTransactionPercentage}
                onChange={(e) => handleSettingsChange({
                  smartRules: {
                    ...settings.smartRules,
                    maxTransactionPercentage: Number(e.target.value)
                  }
                })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-[#1f2544] dark:text-white"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RebalanceSettingsPage;